<template>
  <v-dialog 
    v-model="isOpen" 
    max-width="600px" 
    persistent
    @click:outside="false"
  >
    <v-card>
      <v-card-title class="text-h5 text-error">
        <v-icon icon="mdi-delete-alert" class="me-2" color="error"></v-icon>
        Delete List: {{ listData?.name }}
      </v-card-title>
      
      <v-card-text>
        <v-alert
          type="warning"
          variant="tonal"
          class="mb-4"
          icon="mdi-alert-triangle"
        >
          <v-alert-title>This action cannot be undone!</v-alert-title>
          <div class="mt-2">
            Deleting this list will permanently remove:
            <ul class="mt-2">
              <li><strong>{{ actualItemCount }}</strong> items</li>
              <li><strong>{{ actualGroupCount }}</strong> groups</li>
              <li>All shared links associated with this list</li>
              <li>The list itself and all associated data</li>
            </ul>
          </div>
        </v-alert>

        <v-divider class="my-4"></v-divider>

        <div class="text-subtitle-1 font-weight-medium mb-3">
          <v-icon icon="mdi-content-save" class="me-2"></v-icon>
          Backup Options (Recommended)
        </div>
        
        <div class="d-flex flex-column gap-2">
          <v-btn
            variant="outlined"
            color="primary"
            prepend-icon="mdi-file-download"
            @click="downloadJsonBackup"
            :loading="isExporting"
            block
          >
            Download JSON Backup
            <v-tooltip activator="parent" location="bottom">
              Export list data as JSON file for later import
            </v-tooltip>
          </v-btn>
          
          <v-btn
            variant="outlined"
            color="secondary" 
            prepend-icon="mdi-file-pdf-box"
            @click="downloadPdfBackup"
            :loading="isGeneratingPdf"
            block
          >
            Download PDF Backup
            <v-tooltip activator="parent" location="bottom">
              Generate a printable PDF version of the list
            </v-tooltip>
          </v-btn>
        </div>

        <v-divider class="my-4"></v-divider>

        <v-checkbox
          v-model="confirmationChecked"
          color="error"
          class="text-error"
        >
          <template #label>
            <span class="text-error">
              I understand that this action cannot be undone and will permanently delete all data associated with this list.
            </span>
          </template>
        </v-checkbox>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text="Cancel"
          @click="cancel"
          :disabled="isDeleting"
        ></v-btn>
        <v-btn
          color="error"
          variant="flat"
          prepend-icon="mdi-delete"
          @click="deleteList"
          :disabled="!confirmationChecked"
          :loading="isDeleting"
        >
          Delete List
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useListStore } from '@/stores/list'
import { usePDFGeneration } from '@/composables/usePDFGeneration'
import axios from 'axios'

const props = defineProps({
  modelValue: Boolean,
  listData: Object
})

const emit = defineEmits(['update:modelValue', 'deleted', 'message'])

const listStore = useListStore()
const { generatePDF } = usePDFGeneration()

const isDeleting = ref(false)
const isExporting = ref(false)
const isGeneratingPdf = ref(false)
const confirmationChecked = ref(false)
const fullListData = ref(null)
const loadingCounts = ref(false)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const actualItemCount = computed(() => {
  return fullListData.value?.items?.length || props.listData?.item_count || 0
})

const actualGroupCount = computed(() => {
  return fullListData.value?.groups?.length || props.listData?.group_count || 0
})

// Fetch full list data when dialog opens
const fetchFullListData = async () => {
  if (!props.listData?.id || loadingCounts.value) return
  
  loadingCounts.value = true
  try {
    const response = await axios.get(`/api/packlists/${props.listData.id}`)
    fullListData.value = response.data
  } catch (error) {
    console.error('Failed to fetch full list data:', error)
    // Fallback to summary data
    fullListData.value = null
  } finally {
    loadingCounts.value = false
  }
}

// Watch for dialog opening to fetch data
watch(() => props.modelValue, (newValue) => {
  if (newValue && props.listData) {
    fetchFullListData()
  }
})

const downloadJsonBackup = async () => {
  if (!props.listData) return
  
  isExporting.value = true
  try {
    const response = await axios.get('/api/export', {
      params: {
        packlist_ids: props.listData.id
      }
    })
    
    // Create and download the file
    const blob = new Blob([JSON.stringify(response.data, null, 2)], {
      type: 'application/json'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${props.listData.name}_backup_${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    emit('message', { message: 'JSON backup downloaded successfully!', color: 'success' })
  } catch (error) {
    console.error('Export failed:', error)
    emit('message', { message: 'Failed to create JSON backup', color: 'error' })
  } finally {
    isExporting.value = false
  }
}

const downloadPdfBackup = async () => {
  if (!props.listData) return
  
  isGeneratingPdf.value = true
  try {
    // Use already fetched data or fetch if not available
    let listDataForPdf = fullListData.value
    if (!listDataForPdf) {
      const response = await axios.get(`/api/packlists/${props.listData.id}`)
      listDataForPdf = response.data
    }
    
    await generatePDF(listDataForPdf, {
      filename: `${props.listData.name}_backup_${new Date().toISOString().split('T')[0]}.pdf`,
      showHeader: true,
      showChecked: true
    })
    
    emit('message', { message: 'PDF backup downloaded successfully!', color: 'success' })
  } catch (error) {
    console.error('PDF generation failed:', error)
    emit('message', { message: 'Failed to create PDF backup', color: 'error' })
  } finally {
    isGeneratingPdf.value = false
  }
}

const deleteList = async () => {
  if (!props.listData || !confirmationChecked.value) return
  
  isDeleting.value = true
  try {
    const result = await listStore.deleteList(props.listData.id)
    if (result.success) {
      emit('message', { message: `List "${props.listData.name}" deleted successfully`, color: 'success' })
      emit('deleted', props.listData.id)
      // Add small delay to ensure parent handlers complete
      setTimeout(() => {
        cancel()
      }, 100)
    } else {
      emit('message', { message: result.error || 'Failed to delete list', color: 'error' })
    }
  } catch (error) {
    console.error('Delete failed:', error)
    emit('message', { message: 'Failed to delete list', color: 'error' })
  } finally {
    isDeleting.value = false
  }
}

const cancel = () => {
  if (isDeleting.value) return
  
  // Reset all dialog state
  confirmationChecked.value = false
  fullListData.value = null
  
  // Close the dialog
  isOpen.value = false
  emit('update:modelValue', false)
}
</script>